import React, { useState, useEffect, useCallback } from "react";
import { Divider, Input, Button, Combobox, Field, Text, Option, Menu, MenuTrigger, MenuPopover, MenuList, MenuItem } from "@fluentui/react-components";
import { MoreVerticalRegular, SearchRegular, SignOut24Regular } from "@fluentui/react-icons";
import { useHistory } from 'react-router-dom'
import { formatDateToISOShort } from "../../common/utilities/FormDate";
import { api, signOut } from "../../api/api";
import { DEFAULT_ROOM_FILTERS, ENABLED, EIGHT_AM, FIVE_PM, AFTERNOON, ALLDAY, MORNING} from "../../variables";


import Filters from "../Filters";
import Room from "../Room";
import Stack from "../Stack";
import ConfirmationButton from "../ConfirmationButton";
import { connect, useDispatch } from "react-redux";
import { logoutSuccess } from "../../redux/reducers/authSlice";

import {
  setLoading,
  defaultSuccess,
  defaultCatch,
} from '../../redux/actions';
import { useTranslation } from "react-i18next";

function RoomsPage(props) {
  const {
    setLoading,
    defaultSuccess,
    defaultCatch,
    unformattedDefaultEntryTime,
    unformattedDefaultExitTime
  } = props;

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const [showConfirmationButton, setShowConfirmationButton] = useState(false)
  const [selectedRoom, setSelectedRoom] = useState({});
  const [rooms, setRooms] = useState([]);
  const [filteredRooms, setFilteredRooms] = useState(rooms);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [handlerId, setHandlerId] = useState(null);
  const [currentOrder, setCurrentOrder] = useState(t('alphabetically'));
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState(DEFAULT_ROOM_FILTERS)

  const orderBy = useCallback((rooms = [], order) => {
    if (order === t('by capacity')) {
      return rooms.sort((a, b) => a.capacity - b.capacity)
    }
    return rooms.sort((a, b) => a.name.localeCompare(b.name))
  }, [t])

  const handleOrder = useCallback(order => {
    setSelectedRoom({})
    setShowConfirmationButton(false)
    setCurrentOrder(order)
    setFilteredRooms(orderBy(filteredRooms, order))
  }, [filteredRooms, orderBy])

  const MicButton = (props) => {
    return (
      <Button
        {...props}
        appearance="transparent"
        icon={<SearchRegular />}
        size="small"
      />
    );
  };

  const onUserSelectsRoom = async () => {
    const newAttender = {
      "displayName": selectedRoom.name,
      "emailAddress": selectedRoom.graph_email
    };

    setShowConfirmationButton(false);
    setSelectedRoom({});

    try {
      // Get current attendees
      const attendeesResult = await new Promise((resolve, reject) => {
        Office.context.mailbox.item.requiredAttendees.getAsync(result => {
          if (result.status === Office.AsyncResultStatus.Succeeded) resolve(result);
          else reject(result.error);
        });
      });

      const currentAttendees = attendeesResult.value;
      const roomEmails = rooms.map(room => room.graph_email);
      const attendeesToRemove = currentAttendees.filter(attendee => roomEmails.includes(attendee.emailAddress));

      // Remove existing room attendees
      if (attendeesToRemove.length > 0) {
        await new Promise((resolve, reject) => {
          Office.context.mailbox.item.requiredAttendees.removeAsync(attendeesToRemove, result => {
            if (result.status === Office.AsyncResultStatus.Succeeded) resolve(result);
            else reject(result.error);
          });
        });
        console.log("Salas anteriores eliminadas del evento.");
      }

      // Add the new room
      await new Promise((resolve, reject) => {
        Office.context.mailbox.item.requiredAttendees.addAsync([newAttender], result => {
          if (result.status === Office.AsyncResultStatus.Succeeded) resolve(result);
          else reject(result.error);
        });
      });
      console.log("Sala añadida al evento con éxito.");

      // Set the location
      await new Promise((resolve, reject) => {
        Office.context.mailbox.item.location.setAsync(newAttender.displayName, result => {
          if (result.status === Office.AsyncResultStatus.Succeeded) resolve(result);
          else reject(result.error);
        });
      });
      console.log("Ubicación del evento actualizada.");

    } catch (error) {
      console.error("Ocurrió un error al gestionar la sala en el evento:", error.message || error);
    }
  };

  const onSelectsRoom = selectedRoom => {
    setSelectedRoom(selectedRoom);
    setShowConfirmationButton(true);
  }

  const cleanRooms = useCallback((rooms = []) => {
    // Remove DISABLED rooms
    rooms = rooms.filter(room => room.status === ENABLED && room.graph_email)

    // Remove repeat elements
    const items = []
    const ids = []
    rooms.forEach(r => {
      if (!ids.includes(r.id)) {
        ids.push(r.id)
        items.push(r)
      }
    })
    return items
  }, [])

  const fetchAvailableRooms = useCallback(() => {
    if (!startTime || !endTime) return;

    setLoading(true)
    const _date = formatDateToISOShort(startTime)
    const params = { 
      date: _date, 
      all: true 
    };
    api.get(`workplace/rooms/availability`, { params })
      .then(defaultSuccess)
      .then(({ data }) => {
        if (!data.success) return
        const rooms = cleanRooms(data.rooms)
        setRooms(rooms)
      })
      .catch(error => {
        console.log(error);
        if (error?.response?.status === 401) {
          dispatch(logoutSuccess());
        }
      })
      .finally(() => setLoading(false))
  }, [startTime, endTime, setLoading, defaultSuccess, cleanRooms, dispatch])

  const filterRooms = useCallback(
    (rooms = []) => {
      // Filter text search
      if (search) {
        const _search = search.toLocaleLowerCase();
        rooms = rooms.filter((item) =>
          item.name.toLowerCase().includes(_search)
        );
      }

      // Filter by Site, Floor, Building, Capacity and Amenities
      if (filter.filtered) {
        rooms = rooms?.filter((room) => {
          const matchesSite =
            filter.site === "" || room.site?.id === Number(filter.site);
          const matchesBuilding =
            filter.building === "" ||
            room.building?.id === Number(filter.building);
          const matchesFloor =
            filter.floor === "" || room.floor?.id === Number(filter.floor);
          const matchesCapacity =
            !filter.capacity || room.capacity >= Number(filter.capacity);
          const matchesAmenities =
            filter.amenities?.length === 0 ||
            filter.amenities?.every((amenity) =>
              room.amenities.includes(amenity)
            );

          return (
            matchesSite &&
            matchesBuilding &&
            matchesFloor &&
            matchesCapacity &&
            matchesAmenities
          );
        });
      }

      return rooms;
    },
    [filter, search]
  );


  useEffect(() => {
    if (Office.context.mailbox && Office.context.mailbox.item) {
      const getInitialTimes = async () => {
        const getStartPromise = new Promise((resolve, reject) => {
          Office.context.mailbox.item.start.getAsync((asyncResult) => {
            if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
              resolve(asyncResult.value);
            } else {
              reject(asyncResult.error);
            }
          });
        });

        const getEndPromise = new Promise((resolve, reject) => {
          Office.context.mailbox.item.end.getAsync((asyncResult) => {
            if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
              resolve(asyncResult.value);
            } else {
              reject(asyncResult.error);
            }
          });
        });

        try {
          const [startResult, endResult] = await Promise.all([getStartPromise, getEndPromise]);
          setStartTime(startResult);
          setEndTime(endResult);
        } catch (error) {
          console.error("Error al obtener las fechas iniciales:", error.message);
        }
      };

      const handleTimeChange = (eventAsync) => {
        setStartTime(new Date(eventAsync.start));
        setEndTime(new Date(eventAsync.end));
      };

      getInitialTimes();

      Office.context.mailbox.item.addHandlerAsync(Office.EventType.AppointmentTimeChanged, handleTimeChange, (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          console.error("No se pudo agregar el controlador de eventos.", asyncResult.error.message);
          return;
        }
        setHandlerId(asyncResult.value);
      });
    }

    return () => {
      if (handlerId && Office.context.mailbox && Office.context.mailbox.item) {
        Office.context.mailbox.item.removeHandlerAsync(handlerId, () => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setFilteredRooms(orderBy(filterRooms(rooms), currentOrder))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, rooms, filter])

  useEffect(() => {
    fetchAvailableRooms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startTime, endTime]);

  const handleApplyFilters = (params) => {
    setFilter(params);
    setSelectedRoom({});
    setShowConfirmationButton(false);
  };

  const onSearch = (value) => {
    setSearch(value)
    setSelectedRoom({})
    setShowConfirmationButton(false)
  }
  
  return (
    <Stack direction="column" gap="10px">
      <Stack direction="row" alignItems="center" gap="5px">
        <Input
          size="small"
          contentAfter={<MicButton aria-label="Enter" />}
          appearance="filled-darker"
          placeholder={t("Search")}
          style={{ width: '100%' }}
          onChange={(e, { value }) => onSearch(value)}
          value={search}
        />

        <Button
          appearance="transparent"
          size="small"
          icon={<SignOut24Regular />}
          title={t("logout")}
          aria-label={t("logout")}
          onClick={() => signOut().finally(() => dispatch(logoutSuccess()))}
        />
      </Stack>
      <Filters 
        handleApplyFilters={handleApplyFilters}
      />
      <Divider />
      <Field
        label={
          <Text size={100}>{t("Sort")}</Text>
        }
        orientation="horizontal"
      >
        <Combobox
          style={{ minWidth: "80px" }}
          size="small"
          appearance="filled-darker"
          value={currentOrder}
        >
          <Option value={t('alphabetically')} onClick={() => handleOrder(t('alphabetically'))}>{t('alphabetically')}</Option>
          <Option value={t('by capacity')} onClick={() => handleOrder(t('by capacity'))}>{t('by capacity')}</Option>
        </Combobox>
      </Field>
      <Stack direction="column" gap="16px" style={{ padding: '2px 2px 50px', overflowY: 'scroll', height: 'calc(98vh - 200px)' }}>
        {filteredRooms.map((room) => {
          return (
            <Room
              key={room.id}
              defaultEntryTime={unformattedDefaultEntryTime}
              defaultExitTime={unformattedDefaultExitTime}
              onSelectsRoom={onSelectsRoom}
              selectedRoom={selectedRoom} 
              startTime={startTime}
              endTime={endTime}
              room={room}
            />
          )
        })}
      </Stack>
      <ConfirmationButton
        showConfirmationButton={showConfirmationButton}
        onUserSelectsRoom={onUserSelectsRoom} />
    </Stack>
  );
};

const mapStateToProps = state => {
  return {
    sites: state.workplace.sites,
    minReservationTime: state.profile.company.min_reservation_time,
    unformattedDefaultEntryTime: state.profile.company.workplace_default_entry_time || EIGHT_AM,
    unformattedDefaultExitTime: state.profile.company.workplace_default_exit_time || FIVE_PM,
    askForRoomReservationNotes: state.profile.company.ask_for_room_reservation_notes,
    askForRoomReservationReason: state.profile.company.ask_for_room_reservation_reason,
    askForDeskReservationReason: state.profile.company.ask_for_desk_reservation_reason,
    askForDeskReservationEntryTime: state.profile.company.ask_for_desk_reservation_entry_time,
    workplaceCustomLabel: state.profile.company.workplace_custom_label,
    workplaceCustomIconIndex: state.profile.company.workplace_custom_icon_index,
    enabledWorkplaceCustomIcon: state.profile.company.enabled_workplace_custom_icon,
    enabledWorkplaceRoomReservationsAttendees: state.profile.company.enabled_workplace_room_reservations_attendees,
    askForParkingReservationReason: state.profile.company.ask_for_parking_reservation_reason,
    askForParkingReservationVehicle: state.profile.company.ask_for_parking_reservation_vehicle,
    askForParkingReservationEntryTime: state.profile.company.ask_for_parking_reservation_entry_time
  }
}

const mapDispatchToProps = {
  setLoading,
  defaultSuccess,
  defaultCatch
}

export default connect(mapStateToProps, mapDispatchToProps)(RoomsPage)